import { useState, useEffect, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
	PlusIcon,
	TrashIcon,
	PencilSquareIcon,
	ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, Badge, ConfirmDialog } from '../ui';
import type { Service, ServiceVariant } from '../../stores/servicesStore';
import type { ServiceCategory } from '../../types/serviceCategory';

/** Whether a row differs from what was loaded — drives the row marker and the unsaved-changes guard. */
type VariantState = 'clean' | 'edited' | 'new';

const formatLengthDisplay = (length: string): string =>
	length
		.split('_')
		.map(w => w.charAt(0) + w.slice(1).toLowerCase())
		.join(' ');

/**
 * Names a combination in prose. Size and length are both optional, so this
 * drops whichever is blank rather than producing "Editing  / " or "A  /
 * combination already exists".
 */
const describeCombination = (size: string, length: string): string =>
	[size, length ? formatLengthDisplay(length) : ''].filter(Boolean).join(' / ');

/**
 * Size and length identify a row together: a row carries both codes or carries
 * neither. A half-filled row has no combination to price — the booking page
 * cannot offer it, and the server has no row to resolve a booking against — so
 * it is rejected on entry rather than left to surface as a deposit that does
 * not match the invoice.
 */
const isHalfFilled = (size: string, length: string): boolean => !!size !== !!length;

export interface ServiceModalProps {
	isOpen: boolean;
	onClose: () => void;
	service?: Service | null;
	onSave: (service: Service) => Promise<void>;
	onDelete?: (id: string) => void;
	/**
	 * Categories to file this service under. Provider-only: without it the
	 * picker isn't rendered and whatever category the service already had is
	 * carried through the save untouched.
	 */
	categories?: ServiceCategory[];
	/**
	 * Shows the add-on checkbox. Off by default because the super-admin default
	 * service templates have no add-on flag on their API — rendering it there
	 * would be a control that silently does nothing. With it off, whatever the
	 * service already had is carried through the save untouched.
	 */
	canMarkAddOn?: boolean;
}

const SIZE_OPTIONS = ['XXS', 'XS', 'S', 'SM', 'M', 'L', 'XL', 'JUMBO'];
const LENGTH_OPTIONS = ['SHOULDER', 'BRA', 'MID_BACK', 'WAIST', 'BUTT', 'HIP', 'THIGH', 'KNEE'];

interface VariantFieldsProps {
	size: string;
	length: string;
	duration: string;
	price: string;
	onSize: (value: string) => void;
	onLength: (value: string) => void;
	onDuration: (value: string) => void;
	onPrice: (value: string) => void;
	disabled?: boolean;
	/** Trailing action button(s) — Add for the builder row, Save/Cancel for edit. */
	children: ReactNode;
}

/**
 * The size / length / duration / price input grid, shared by the "Add
 * Combination" builder and the inline edit row. Keeping both on one component
 * means the two can never drift apart — the edit form matches the add design
 * (AC10) for free, and there's only one place inputs are defined.
 */
const VariantFields = ({
	size,
	length,
	duration,
	price,
	onSize,
	onLength,
	onDuration,
	onPrice,
	disabled,
	children,
}: VariantFieldsProps) => (
	<div className="service-modal__input-row">
		<div>
			<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
				Size <span className="service-modal__optional">(with length)</span>
			</label>
			<select
				className="provider-form__input"
				value={size}
				onChange={e => onSize(e.target.value)}
				disabled={disabled}
			>
				<option value="">— None —</option>
				{SIZE_OPTIONS.map(option => (
					<option key={option} value={option}>
						{option}
					</option>
				))}
			</select>
		</div>
		<div>
			<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
				Length <span className="service-modal__optional">(with size)</span>
			</label>
			<select
				className="provider-form__input"
				value={length}
				onChange={e => onLength(e.target.value)}
				disabled={disabled}
			>
				<option value="">— None —</option>
				{LENGTH_OPTIONS.map(option => (
					<option key={option} value={option}>
						{formatLengthDisplay(option)}
					</option>
				))}
			</select>
		</div>
		<div>
			<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
				Duration (min)
			</label>
			<input
				type="number"
				className="provider-form__input"
				placeholder="180"
				min={5}
				step={5}
				value={duration}
				onChange={e => onDuration(e.target.value)}
				disabled={disabled}
			/>
		</div>
		<div>
			<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Price ($)</label>
			<input
				type="number"
				className="provider-form__input"
				placeholder="0"
				min={0}
				step={0.01}
				value={price}
				onChange={e => onPrice(e.target.value)}
				disabled={disabled}
			/>
		</div>
		<div className="flex items-end gap-2">{children}</div>
	</div>
);

const ServiceModal = ({
	isOpen,
	onClose,
	service,
	onSave,
	onDelete,
	categories,
	canMarkAddOn = false,
}: ServiceModalProps) => {
	const isEditMode = !!service;
	const canPickCategory = !!categories;

	// Form state
	const [serviceName, setServiceName] = useState('');
	const [description, setDescription] = useState('');
	const [categoryId, setCategoryId] = useState<number | null>(null);
	const [addOn, setAddOn] = useState(false);
	const [variants, setVariants] = useState<ServiceVariant[]>([]);

	// Save loading state
	const [isSaving, setIsSaving] = useState(false);

	// Current variant input row (the "Add Combination" builder)
	const [currentSize, setCurrentSize] = useState('');
	const [currentLength, setCurrentLength] = useState('');
	const [currentDuration, setCurrentDuration] = useState('');
	const [currentPrice, setCurrentPrice] = useState('');

	// Inline edit state. editingIndex points at the row being edited in place;
	// its fields are kept separate from the builder above so the two never
	// interfere with each other.
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [editSize, setEditSize] = useState('');
	const [editLength, setEditLength] = useState('');
	const [editDuration, setEditDuration] = useState('');
	const [editPrice, setEditPrice] = useState('');

	// Snapshot of the combinations as loaded, keyed by their stable session id.
	// Used to tell which rows changed (the row markers) and whether the modal
	// has anything unsaved (the close guard). Everything in this modal is staged
	// locally until "Update Service" — nothing hits the backend before that.
	const [initialById, setInitialById] = useState<Map<string, ServiceVariant>>(new Map());

	// Discard-changes confirmation shown when closing with unsaved edits.
	const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

	// Reset form when modal opens/closes or service changes
	useEffect(() => {
		if (isOpen) {
			if (service) {
				setServiceName(service.name);
				setDescription(service.description);
				setCategoryId(service.categoryId ?? null);
				setAddOn(service.addOn);
				setVariants([...service.variants]);
				const snapshot = new Map<string, ServiceVariant>();
				service.variants.forEach(v => {
					if (v.id) snapshot.set(v.id, { ...v });
				});
				setInitialById(snapshot);
			} else {
				setServiceName('');
				setDescription('');
				setCategoryId(null);
				setAddOn(false);
				setVariants([]);
				setInitialById(new Map());
			}
			setShowDiscardConfirm(false);
			setCurrentSize('');
			setCurrentLength('');
			setCurrentDuration('');
			setCurrentPrice('');
			setEditingIndex(null);
			setEditSize('');
			setEditLength('');
			setEditDuration('');
			setEditPrice('');
		}
	}, [isOpen, service]);

	/**
	 * Shared validation for both add and edit, so the rules (required fields,
	 * positive duration/price, no duplicate size+length) stay identical (AC7).
	 * `excludeIndex` lets an edit skip the row being edited in the duplicate
	 * check — otherwise changing only the price would falsely collide with itself.
	 * Returns the parsed numbers on success, or null after toasting the error.
	 */
	const validateCombination = (
		size: string,
		length: string,
		durationRaw: string,
		priceRaw: string,
		excludeIndex: number | null
	): { duration: number; price: number } | null => {
		const duration = parseInt(durationRaw) || 0;
		const price = parseFloat(priceRaw) || 0;

		if (duration <= 0) {
			toast.error('Please enter a valid duration');
			return null;
		}
		if (price <= 0) {
			toast.error('Please enter a valid price');
			return null;
		}
		if (isHalfFilled(size, length)) {
			toast.error(
				size
					? 'Pick a length too, or clear the size for one flat price'
					: 'Pick a size too, or clear the length for one flat price'
			);
			return null;
		}

		const exists = variants.find(
			(v, i) => i !== excludeIndex && v.size === size && v.length === length
		);
		if (exists) {
			toast.error(
				size || length
					? `A ${describeCombination(size, length)} combination already exists`
					: 'A combination with no size or length is already added'
			);
			return null;
		}

		return { duration, price };
	};

	const addVariant = () => {
		const result = validateCombination(
			currentSize,
			currentLength,
			currentDuration,
			currentPrice,
			null
		);
		if (!result) return;

		setVariants([
			...variants,
			{
				id: `temp-${Date.now()}`,
				size: currentSize,
				length: currentLength,
				duration: result.duration,
				price: result.price,
			},
		]);

		// Clear price and duration for quick consecutive adds (keep size/length)
		setCurrentDuration('');
		setCurrentPrice('');
	};

	const removeVariant = (index: number) => {
		setVariants(variants.filter((_, i) => i !== index));
	};

	const startEdit = (index: number) => {
		const variant = variants[index];
		setEditSize(variant.size);
		setEditLength(variant.length);
		setEditDuration(String(variant.duration));
		setEditPrice(String(variant.price));
		setEditingIndex(index);
	};

	const cancelEdit = () => {
		setEditingIndex(null);
		setEditSize('');
		setEditLength('');
		setEditDuration('');
		setEditPrice('');
	};

	const saveEdit = () => {
		if (editingIndex === null) return;

		const result = validateCombination(
			editSize,
			editLength,
			editDuration,
			editPrice,
			editingIndex
		);
		if (!result) return;

		// Only the edited row changes; every other combination is left untouched (AC6).
		setVariants(
			variants.map((v, i) =>
				i === editingIndex
					? {
							...v,
							size: editSize,
							length: editLength,
							duration: result.duration,
							price: result.price,
						}
					: v
			)
		);
		cancelEdit();
	};

	const handleSave = async () => {
		if (!serviceName.trim()) {
			toast.error('Please enter a service name');
			return;
		}

		if (variants.length === 0) {
			toast.error('Please add at least one pricing combination');
			return;
		}

		if (editingIndex !== null) {
			toast.error('Please save or cancel the combination you are editing first');
			return;
		}

		// Rows added before the pairing rule existed reach this modal already
		// half-filled, and saving an untouched service would quietly persist them.
		// Name the first one so the provider knows which row to fix.
		const halfFilled = variants.find(v => isHalfFilled(v.size, v.length));
		if (halfFilled) {
			toast.error(
				`The ${describeCombination(halfFilled.size, halfFilled.length)} row needs ` +
					`${halfFilled.size ? 'a length' : 'a size'}, or clear both for one flat price`
			);
			return;
		}

		// With no picker rendered, the service's existing category rides along
		// rather than being blanked by the save.
		const selectedCategory = categories?.find(c => c.id === categoryId) ?? null;

		const serviceData: Service = {
			id: service?.id || '',
			name: serviceName.trim(),
			description: description.trim(),
			status: service?.status || 'ACTIVE',
			category: canPickCategory
				? (selectedCategory?.name ?? null)
				: (service?.category ?? null),
			categoryId: canPickCategory ? categoryId : (service?.categoryId ?? null),
			addOn: canMarkAddOn ? addOn : (service?.addOn ?? false),
			variants,
		};

		setIsSaving(true);
		try {
			await onSave(serviceData);
			onClose();
		} catch {
			// Error toast is handled by the caller
		} finally {
			setIsSaving(false);
		}
	};

	const handleDelete = () => {
		if (!service || !onDelete) return;
		onDelete(service.id);
		onClose();
	};

	const formatDuration = (mins: number): string => {
		const hours = Math.floor(mins / 60);
		const remaining = mins % 60;
		if (hours === 0) return `${remaining}m`;
		if (remaining === 0) return `${hours}h`;
		return `${hours}h ${remaining}m`;
	};

	// Compare a row to its loaded values: 'new' if added this session, 'edited'
	// if a loaded row's fields changed, 'clean' otherwise (including edited back
	// to the original).
	const getVariantState = (variant: ServiceVariant): VariantState => {
		const original = variant.id ? initialById.get(variant.id) : undefined;
		if (!original) return 'new';
		const changed =
			original.size !== variant.size ||
			original.length !== variant.length ||
			original.duration !== variant.duration ||
			original.price !== variant.price;
		return changed ? 'edited' : 'clean';
	};

	// Anything the provider would lose by closing now. A pure deletion shows up
	// as a length mismatch even when every remaining row is clean.
	const hasUnsavedChanges =
		serviceName.trim() !== (service?.name ?? '').trim() ||
		description.trim() !== (service?.description ?? '').trim() ||
		(canPickCategory && categoryId !== (service?.categoryId ?? null)) ||
		(canMarkAddOn && addOn !== (service?.addOn ?? false)) ||
		variants.length !== initialById.size ||
		variants.some(v => getVariantState(v) !== 'clean') ||
		editingIndex !== null;

	const handleRequestClose = () => {
		if (isSaving) return;
		if (hasUnsavedChanges) {
			setShowDiscardConfirm(true);
		} else {
			onClose();
		}
	};

	const isEditingRow = editingIndex !== null;

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleRequestClose}
			title={isEditMode ? 'Edit Service' : 'Add New Service'}
			size="xl"
		>
			<div className="space-y-5">
				{/* Service Name */}
				<div className="provider-form__group">
					<label className="provider-form__label">
						Service Name<span className="provider-form__required">*</span>
					</label>
					<input
						type="text"
						className="provider-form__input"
						placeholder="e.g., Box Braids"
						value={serviceName}
						onChange={e => setServiceName(e.target.value)}
					/>
				</div>

				{/* Description */}
				<div className="provider-form__group">
					<label className="provider-form__label">Description</label>
					<textarea
						className="provider-form__input"
						placeholder="Describe this service..."
						rows={2}
						value={description}
						onChange={e => setDescription(e.target.value)}
					/>
				</div>

				{/* Category — categories themselves are created from the strip on
				    the Services tab, so this only ever files the service. */}
				{canPickCategory && (
					<div className="provider-form__group">
						<label className="provider-form__label">Category</label>
						<select
							className="provider-form__input"
							value={categoryId ?? ''}
							onChange={e =>
								setCategoryId(e.target.value ? Number(e.target.value) : null)
							}
						>
							<option value="">— No category —</option>
							{categories.map(category => (
								<option key={category.id} value={category.id}>
									{category.name}
								</option>
							))}
						</select>
						{categories.length === 0 && (
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
								No categories yet — create one from the Services list.
							</p>
						)}
					</div>
				)}

				{/* Add-on flag. What the customer-facing booking link does with it
				    is the backend's call — here it is simply an attribute of the
				    service, like its status. */}
				{canMarkAddOn && (
					<div className="provider-form__group">
						<label className="service-modal__addon">
							<input
								type="checkbox"
								className="service-modal__addon-box"
								checked={addOn}
								onChange={e => setAddOn(e.target.checked)}
							/>
							<span className="service-modal__addon-text">
								<span className="service-modal__addon-title">
									This service is an Add-on
								</span>
								<span className="service-modal__addon-desc">
									Add-ons are offered as optional extras after the main service on
									your paid booking link (wash, takedown, trim, beads). An add-on
									has a single price — size and length are optional.
								</span>
							</span>
						</label>
					</div>
				)}

				{/* Pricing Combinations Section */}
				<div className="service-modal__combinations">
					<h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
						Pricing Combinations<span className="provider-form__required">*</span>
					</h4>
					<p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
						Set size and length together, or leave both blank for one flat price.
					</p>

					{/* Add-combination builder */}
					<VariantFields
						size={currentSize}
						length={currentLength}
						duration={currentDuration}
						price={currentPrice}
						onSize={setCurrentSize}
						onLength={setCurrentLength}
						onDuration={setCurrentDuration}
						onPrice={setCurrentPrice}
					>
						<Button variant="primary" size="sm" onClick={addVariant} className="w-full">
							<PlusIcon className="w-4 h-4 mr-1" />
							Add
						</Button>
					</VariantFields>

					{/* Combinations table */}
					{variants.length > 0 ? (
						<div className="service-modal__table-wrapper">
							<div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
								Added Combinations ({variants.length})
							</div>
							<div className="service-modal__table-scroll">
								<table className="service-modal__table">
									<thead>
										<tr>
											<th>Size</th>
											<th>Length</th>
											<th className="text-right">Duration</th>
											<th className="text-right">Price</th>
											<th className="w-20"></th>
										</tr>
									</thead>
									<tbody>
										{variants.map((variant, idx) =>
											editingIndex === idx ? (
												<tr
													key={variant.id || idx}
													className="service-modal__edit-row"
												>
													<td colSpan={5}>
														<div className="text-xs font-semibold text-indigo-600 dark:text-indigo-300 mb-2">
															Editing{' '}
															{describeCombination(
																variant.size,
																variant.length
															) || 'combination'}
														</div>
														<VariantFields
															size={editSize}
															length={editLength}
															duration={editDuration}
															price={editPrice}
															onSize={setEditSize}
															onLength={setEditLength}
															onDuration={setEditDuration}
															onPrice={setEditPrice}
														>
															<Button
																variant="primary"
																size="sm"
																onClick={saveEdit}
																className="flex-1"
															>
																Done
															</Button>
															<Button
																variant="secondary"
																size="sm"
																onClick={cancelEdit}
																className="flex-1"
															>
																Cancel
															</Button>
														</VariantFields>
													</td>
												</tr>
											) : (
												<tr key={variant.id || idx}>
													<td>
														<div className="flex items-center gap-2">
															{variant.size ? (
																<Badge variant="primary">
																	{variant.size}
																</Badge>
															) : (
																<span
																	className="service-modal__no-value"
																	title="No size"
																>
																	—
																</span>
															)}
															{getVariantState(variant) === 'new' && (
																<span className="service-modal__row-flag service-modal__row-flag--new">
																	New
																</span>
															)}
															{getVariantState(variant) ===
																'edited' && (
																<span className="service-modal__row-flag service-modal__row-flag--edited">
																	Edited
																</span>
															)}
														</div>
													</td>
													<td>
														{variant.length ? (
															<Badge variant="info">
																{formatLengthDisplay(
																	variant.length
																)}
															</Badge>
														) : (
															<span
																className="service-modal__no-value"
																title="No length"
															>
																—
															</span>
														)}
													</td>
													<td className="text-right text-sm text-gray-600 dark:text-gray-300">
														{formatDuration(variant.duration)}
													</td>
													<td className="text-right text-sm font-semibold text-green-600 dark:text-green-400">
														${variant.price.toFixed(2)}
													</td>
													<td className="text-center">
														<div className="flex items-center justify-center gap-1">
															<button
																type="button"
																onClick={() => startEdit(idx)}
																disabled={isEditingRow}
																title="Edit combination"
																className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
															>
																<PencilSquareIcon className="w-4 h-4" />
															</button>
															<button
																type="button"
																onClick={() => removeVariant(idx)}
																disabled={isEditingRow}
																title="Delete combination"
																className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
															>
																<TrashIcon className="w-4 h-4" />
															</button>
														</div>
													</td>
												</tr>
											)
										)}
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<div className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">
							No combinations added yet. Add your first pricing combination above.
						</div>
					)}
				</div>

				{/* Action Buttons */}
				<div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					{isEditMode && onDelete && (
						<Button
							variant="danger"
							onClick={handleDelete}
							className="sm:mr-auto"
							disabled={isSaving}
						>
							Delete Service
						</Button>
					)}
					<div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:ml-auto">
						{hasUnsavedChanges && (
							<span className="flex items-center justify-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
								<ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
								Unsaved — click {isEditMode ? 'Update' : 'Add'} Service to save
							</span>
						)}
						<Button
							variant="secondary"
							onClick={handleRequestClose}
							disabled={isSaving}
						>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSave} isLoading={isSaving}>
							{isEditMode ? 'Update Service' : 'Add Service'}
						</Button>
					</div>
				</div>
			</div>

			{/* Guards against losing staged (not-yet-persisted) changes on close. */}
			<ConfirmDialog
				isOpen={showDiscardConfirm}
				onClose={() => setShowDiscardConfirm(false)}
				onConfirm={() => {
					setShowDiscardConfirm(false);
					onClose();
				}}
				title="Discard unsaved changes?"
				message="Your changes to this service haven't been saved yet. Closing now will discard them."
				confirmLabel="Discard"
				cancelLabel="Keep editing"
				variant="danger"
			/>
		</Modal>
	);
};

export default ServiceModal;
